import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminLayout } from "../../components/admin/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Checkbox } from "../../components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Skeleton } from "../../components/ui/skeleton";
import { EmptyState } from "../../components/EmptyState";
import { SupplierSelect } from "../../components/SupplierSelect";
import { PackageOpen, Search, Printer, Truck, FileText } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { fmtDate, waitingColor } from "../../lib/statuses";
import { useAuth } from "../../context/AuthContext";

export default function ReadyToShip() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const canManage = hasRole("SUPER_ADMIN", "WARRANTY_MANAGER", "WARRANTY_STAFF");
  const [tab, setTab] = useState("ready");
  const [items, setItems] = useState(null);
  const [batches, setBatches] = useState(null);
  const [search, setSearch] = useState("");
  const [supplierFilter, setSupplierFilter] = useState("");
  const [selected, setSelected] = useState([]);
  const [generating, setGenerating] = useState(false);

  const loadReady = async () => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (supplierFilter) params.set("supplier_id", supplierFilter);
    const { data } = await api.get(`/admin/ready-to-ship?${params}`);
    setItems(data);
    setSelected((prev) => prev.filter((id) => data.some((d) => d.claim_id === id)));
  };
  const loadBatches = async () => { const { data } = await api.get("/admin/shipping-batches"); setBatches(data); };

  useEffect(() => { const t = setTimeout(loadReady, 200); return () => clearTimeout(t); }, [search, supplierFilter]);
  useEffect(() => { loadBatches(); }, []);

  const activeSupplier = selected.length ? items?.find((i) => i.claim_id === selected[0])?.supplier_id : null;

  const toggle = (item) => {
    if (selected.includes(item.claim_id)) { setSelected(selected.filter((id) => id !== item.claim_id)); return; }
    if (selected.length && item.supplier_id !== activeSupplier) {
      toast.error("All selected RMAs must belong to the same supplier"); return;
    }
    setSelected([...selected, item.claim_id]);
  };

  const generate = async (force = false) => {
    if (!selected.length) return;
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/shipping-batches", { claim_ids: selected, force });
      toast.success(`Created ${data.ship_number}`);
      setSelected([]);
      navigate(`/admin/shipping-batches/${data.id}/slip`);
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      if (e.response?.status === 409 && detail.startsWith("SERVICE_CENTRE_MISSING")) {
        if (window.confirm("This supplier has no Service Centre address. Generate the shipping slip anyway using the available address?")) {
          setGenerating(false); return generate(true);
        }
      } else { toast.error(formatApiError(detail) || "Failed to generate slip"); }
    } finally { setGenerating(false); }
  };

  const selItems = (items || []).filter((i) => selected.includes(i.claim_id));
  const selSupplierName = selItems[0]?.supplier_name;

  return (
    <AdminLayout title="Ready to Ship">
      <div className="mb-5 flex gap-2">
        <button data-testid="rts-tab-ready" onClick={() => setTab("ready")} className={`rounded-lg px-4 py-2 text-sm font-medium ${tab === "ready" ? "bg-blue-600 text-white" : "border border-slate-200 bg-white text-slate-600"}`}>Ready to Ship</button>
        <button data-testid="rts-tab-batches" onClick={() => setTab("batches")} className={`rounded-lg px-4 py-2 text-sm font-medium ${tab === "batches" ? "bg-blue-600 text-white" : "border border-slate-200 bg-white text-slate-600"}`}>Shipping Batches</button>
      </div>

      {tab === "ready" && (
        <>
          <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input data-testid="rts-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search RMA, supplier, product, part, SKU, serial, customer..." className="pl-9" />
            </div>
            <div className="w-full lg:w-64"><SupplierSelect value={supplierFilter} onChange={setSupplierFilter} placeholder="All Suppliers" testId="rts-supplier-filter" /></div>
          </div>

          {selected.length > 0 && (
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3" data-testid="rts-selection-bar">
              <div className="text-sm text-slate-700"><span className="font-semibold">{selected.length}</span> selected · Supplier: <span className="font-semibold">{selSupplierName}</span></div>
              <Button data-testid="generate-slip-btn" disabled={generating || !canManage} onClick={() => generate(false)} className="gap-1.5 bg-blue-600 hover:bg-blue-700"><FileText className="h-4 w-4" /> {generating ? "Generating..." : "Generate Shipping Slip"}</Button>
            </div>
          )}

          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-10"></TableHead>
                    <TableHead>RMA #</TableHead><TableHead>Supplier</TableHead><TableHead>Product</TableHead><TableHead>Part Name</TableHead>
                    <TableHead>SKU</TableHead><TableHead>Serial</TableHead><TableHead>Customer</TableHead><TableHead>Fault</TableHead>
                    <TableHead>Date Ready</TableHead><TableHead className="text-center">Waiting</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items === null && Array.from({ length: 6 }).map((_, i) => <TableRow key={i}><TableCell colSpan={11}><Skeleton className="h-6 w-full" /></TableCell></TableRow>)}
                  {items?.map((it) => {
                    const disabled = selected.length > 0 && it.supplier_id !== activeSupplier && !selected.includes(it.claim_id);
                    return (
                      <TableRow key={it.claim_id} data-testid={`rts-row-${it.rma_number}`} className={`hover:bg-blue-50/40 ${disabled ? "opacity-40" : ""}`}>
                        <TableCell><Checkbox checked={selected.includes(it.claim_id)} onCheckedChange={() => toggle(it)} data-testid={`rts-check-${it.rma_number}`} /></TableCell>
                        <TableCell><button data-testid={`rts-rma-${it.rma_number}`} onClick={() => navigate(`/admin/warranty/claims/${it.claim_id}`)} className="font-mono text-sm font-semibold text-blue-700 hover:underline">{it.rma_number}</button></TableCell>
                        <TableCell className="text-sm text-slate-700">{it.supplier_name || "—"}</TableCell>
                        <TableCell className="max-w-[150px] truncate text-sm text-slate-700">{it.product_name}</TableCell>
                        <TableCell className="text-sm text-slate-600">{it.part_name || "—"}</TableCell>
                        <TableCell className="font-mono text-xs text-slate-500">{it.sku || "—"}</TableCell>
                        <TableCell className="font-mono text-xs text-slate-500">{it.serial_number || "—"}</TableCell>
                        <TableCell className="text-sm text-slate-700">{it.customer_name}</TableCell>
                        <TableCell className="max-w-[160px] truncate text-sm text-slate-500">{it.fault || "—"}</TableCell>
                        <TableCell className="text-sm text-slate-600">{fmtDate(it.ready_date)}</TableCell>
                        <TableCell className="text-center"><span className={`text-sm font-bold ${waitingColor(it.waiting_days, false)}`}>{it.waiting_days}d</span></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            {items?.length === 0 && <EmptyState icon={PackageOpen} title="Nothing ready to ship" description="Mark an RMA as Ready to Ship from its case page to queue it here." testId="rts-empty" />}
          </div>
        </>
      )}

      {tab === "batches" && <BatchHistory batches={batches} onReload={() => { loadBatches(); loadReady(); }} canManage={canManage} navigate={navigate} />}
    </AdminLayout>
  );
}

function BatchHistory({ batches, onReload, canManage, navigate }) {
  const markShipped = async (b) => {
    try { await api.put(`/admin/shipping-batches/${b.id}/mark-shipped`, {}); toast.success(`${b.ship_number} marked as shipped`); onReload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Batch #</TableHead><TableHead>Supplier</TableHead><TableHead className="text-center">Items</TableHead>
              <TableHead>Created</TableHead><TableHead>By</TableHead><TableHead>Shipping Date</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {batches === null && Array.from({ length: 4 }).map((_, i) => <TableRow key={i}><TableCell colSpan={8}><Skeleton className="h-6 w-full" /></TableCell></TableRow>)}
            {batches?.map((b) => (
              <TableRow key={b.id} data-testid={`batch-row-${b.ship_number}`} className="hover:bg-blue-50/40">
                <TableCell className="font-mono text-sm font-semibold text-slate-900">{b.ship_number}</TableCell>
                <TableCell className="text-sm text-slate-700">{b.supplier_name}</TableCell>
                <TableCell className="text-center text-sm">{b.items?.length || 0}</TableCell>
                <TableCell className="text-sm text-slate-600">{fmtDate(b.created_date)}</TableCell>
                <TableCell className="text-sm text-slate-500">{b.created_by}</TableCell>
                <TableCell className="text-sm text-slate-600">{b.shipping_date ? fmtDate(b.shipping_date) : "—"}</TableCell>
                <TableCell>{b.shipped ? <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">SHIPPED</span> : <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">NOT SHIPPED</span>}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" data-testid={`batch-view-${b.ship_number}`} onClick={() => navigate(`/admin/shipping-batches/${b.id}/slip`)} className="gap-1"><Printer className="h-3.5 w-3.5" /> View / Print</Button>
                    {canManage && !b.shipped && <Button size="sm" data-testid={`batch-ship-${b.ship_number}`} onClick={() => markShipped(b)} className="gap-1 bg-blue-600 hover:bg-blue-700"><Truck className="h-3.5 w-3.5" /> Mark Shipped</Button>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {batches?.length === 0 && <EmptyState icon={Truck} title="No shipping batches yet" description="Generate a shipping slip from the Ready to Ship tab." testId="batch-empty" />}
    </div>
  );
}
